import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Download, LogOut, Package, ChevronDown, Calendar, RefreshCw, Navigation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCustomerAuth, API } from '../context/CustomerAuthContext';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import PasskeyEnrollPrompt from '../components/PasskeyEnrollPrompt';
import OrderLookupCard from '../components/OrderLookupCard';
import PushBell from '../components/PushBell';
import { formatDateTime } from '../lib/dateFormat';
import Rial from '../components/Rial';

const statusColors = {
  draft: 'bg-neutral-200 text-neutral-700',
  new: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-amber-100 text-amber-700',
  assigned: 'bg-indigo-100 text-indigo-700',
  picked_up: 'bg-amber-100 text-amber-700',
  out_for_delivery: 'bg-purple-100 text-purple-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-red-100 text-red-700',
};

const MyOrders = () => {
  const { token, email, displayName, logout, authHeaders } = useCustomerAuth();
  const { add, setOpen } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [reordering, setReordering] = useState(null);
  // Feb 2026 — inline-edit customer confirmation flow.
  const [confirmingEdits, setConfirmingEdits] = useState({});
  const { toast } = useToast();

  const reload = () => {
    if (!token) return;
    axios
      .get(`${API}/customer/orders`, { headers: authHeaders })
      .then(({ data }) => setOrders(data))
      .catch((e) => console.error(e));
  };

  const confirmOrderEdits = async (o) => {
    setConfirmingEdits((p) => ({ ...p, [o.id]: true }));
    try {
      await axios.post(
        `${API}/customer/orders/${o.id}/confirm-edits`, {},
        { headers: authHeaders },
      );
      toast({ title: 'Order confirmed', description: `Order ${o.order_number || o.id.slice(0, 8)} confirmed.` });
      reload();
    } catch (err) {
      toast({ title: 'Confirm failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally {
      setConfirmingEdits((p) => ({ ...p, [o.id]: false }));
    }
  };

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/customer/orders`, { headers: authHeaders })
      .then(({ data }) => setOrders(data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [token]);

  if (!token) return <Navigate to="/login" replace />;

  const download = async (order) => {
    try {
      const res = await axios.get(`${API}/orders/${order.id}/invoice`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${order.order_number || order.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Download failed', description: 'Please try again.' });
    }
  };

  const reorder = async (order) => {
    setReordering(order.id);
    try {
      // Validate items still exist & are in stock against the current catalog
      const { data: catalog } = await axios.get(`${API}/products`);
      const byId = new Map(catalog.map((p) => [p.id, p]));

      let added = 0;
      let skipped = 0;
      for (const i of order.items) {
        const live = byId.get(i.id);
        if (!live || live.in_stock === false) {
          skipped += 1;
          continue;
        }
        // Add with the live price/uom so customer always pays current price
        add(
          {
            id: live.id,
            name: live.name,
            size: live.size,
            price: live.price,
            image: live.image,
            uom: live.uom,
            vat_applicable: live.vat_applicable,
          },
          i.qty,
        );
        added += 1;
      }

      if (added === 0) {
        toast({
          title: 'Nothing to reorder',
          description: 'These items are no longer available.',
        });
        return;
      }
      toast({
        title: 'Added to cart',
        description:
          skipped > 0
            ? `${added} item${added === 1 ? '' : 's'} added · ${skipped} unavailable`
            : `${added} item${added === 1 ? '' : 's'} from order ${
                order.order_number || order.id.slice(0, 8)
              } added to your cart.`,
      });
      setOpen(true);
    } catch (err) {
      toast({ title: 'Reorder failed', description: 'Please try again.' });
    } finally {
      setReordering(null);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[#F8FAFC]">
      <section className="bg-white border-b border-slate-100 py-10 lg:py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold tracking-[0.2em] text-[#0078D4] uppercase mb-2">
              Trade account
            </div>
            <h1 className="layout-page-title font-display text-3xl md:text-4xl text-slate-900" data-testid="my-orders-greeting">
              Hello, {displayName} <span aria-hidden="true">👋</span>
            </h1>
            <p className="layout-page-subtitle text-sm text-slate-500 mt-1">
              {email}
              {!loading && orders.length > 0 && (
                <span className="text-neutral-400"> · {orders.length} order{orders.length === 1 ? '' : 's'}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <PushBell role="customer" authHeaders={authHeaders} autoPrompt />
            <Button onClick={logout} variant="outline" className="rounded-full border-slate-300" data-testid="my-orders-signout">
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
        <div className="mb-6">
          <PasskeyEnrollPrompt />
        </div>
        <OrderLookupCard />
        {loading ? (
          <div className="text-center text-neutral-500 py-16">Loading your orders\u2026</div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center">
            <Package className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-[#0F172A]">No orders yet</h3>
            <p className="text-sm text-neutral-500 mt-1">Once you place an order it will appear here.</p>
            <Link to="/">
              <Button className="mt-5 rounded-full bg-[#0078D4] hover:bg-[#005A9E]">
                Browse products
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => {
              const isOpen = expanded === o.id;
              return (
                <div key={o.id} className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
                  <div
                    className="p-5 flex flex-wrap items-center gap-4 cursor-pointer hover:bg-neutral-50"
                    onClick={() => setExpanded(isOpen ? null : o.id)}
                  >
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-semibold text-[#0F172A]">{o.order_number || `Order #${o.id.slice(0, 8)}`}</div>
                      <div className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5">
                        <Calendar className="w-3 h-3" />
                        {formatDateTime(o.created_at)}
                      </div>
                    </div>
                    <div className="text-sm text-neutral-600">
                      {o.items.reduce((s, i) => s + i.qty, 0)} items
                    </div>
                    <div className="font-bold text-[#0F172A]"><Rial /> {o.total.toFixed(3)}</div>
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${statusColors[o.status]}`}>
                      {o.status}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                  {isOpen && (
                    <div className="px-5 pb-5 border-t border-neutral-100 pt-4">
                      {/* Feb 2026 — Confirm Order banner. Shows when admin/
                          staff edited the order inline; the customer must
                          review + confirm before the workflow resumes. */}
                      {o.edit_pending_confirmation && (
                        <div
                          className="mb-4 rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-3 flex flex-wrap items-start gap-3"
                          data-testid={`customer-order-${o.id}-confirm-edit-banner`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-amber-900">
                              This order was edited — please review and confirm
                            </div>
                            <div className="text-[11px] text-amber-800 mt-0.5">
                              Check the updated items and total below. The order stays paused until you confirm.
                            </div>
                          </div>
                          <Button
                            onClick={() => confirmOrderEdits(o)}
                            disabled={!!confirmingEdits[o.id]}
                            className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid={`customer-order-${o.id}-confirm-edit-btn`}
                          >
                            {confirmingEdits[o.id] ? 'Confirming…' : 'Confirm Order'}
                          </Button>
                        </div>
                      )}
                      <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-3">Items</div>
                      <div className="space-y-2 text-sm" data-testid={`customer-order-${o.id}-items-table`}>
                        <div className="overflow-x-auto rounded-lg border border-neutral-200">
                          <table className="w-full text-left">
                            <thead>
                              <tr className="bg-neutral-50 text-[10px] uppercase tracking-wide text-neutral-500">
                                <th className="px-3 py-2 font-semibold w-10 text-right tabular-nums">#</th>
                                <th className="px-3 py-2 font-semibold w-[110px]">Item Code</th>
                                <th className="px-3 py-2 font-semibold">Item Name</th>
                                <th className="px-3 py-2 font-semibold text-right w-[90px]">Qty</th>
                                <th className="px-3 py-2 font-semibold text-right w-[110px]">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {o.items.map((i, idx) => (
                                <tr key={idx} className="border-t border-neutral-100 align-top">
                                  <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{(i?.line_no != null && i?.line_no !== '') ? i.line_no : (idx + 1)}</td>
                                  <td className="px-3 py-2 font-mono text-xs text-neutral-600">{i.sku || '—'}</td>
                                  <td className="px-3 py-2 text-[#0F172A]">
                                    {i.name}
                                    {i.size && <span className="text-neutral-500 ml-1">({i.size})</span>}
                                  </td>
                                  <td className="px-3 py-2 tabular-nums text-right whitespace-nowrap">
                                    {i.qty} <span className="text-neutral-500">{i.uom || 'ctn'}</span>
                                  </td>
                                  <td className="px-3 py-2 font-medium tabular-nums text-right whitespace-nowrap"><Rial /> {(i.price * i.qty).toFixed(3)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {o.subtotal > 0 && (
                          <div className="flex justify-between pt-2 text-neutral-600">
                            <span>Subtotal</span>
                            <span><Rial /> {o.subtotal.toFixed(3)}</span>
                          </div>
                        )}
                        {o.vat > 0 && (
                          <div className="flex justify-between text-neutral-600">
                            <span>VAT (5%)</span>
                            <span><Rial /> {o.vat.toFixed(3)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-[#0F172A] pt-2 mt-2 border-t border-neutral-200">
                          <span>Total</span>
                          <span><Rial /> {o.total.toFixed(3)}</span>
                        </div>
                      </div>
                      <div className="mt-5 flex flex-wrap items-center gap-3">
                        <Button
                          onClick={() => reorder(o)}
                          disabled={reordering === o.id || !o.items?.length}
                          className="rounded-full bg-[#0F172A] hover:bg-black text-white"
                          data-testid={`reorder-btn-${o.id}`}
                        >
                          <RefreshCw className={`w-4 h-4 mr-1 ${reordering === o.id ? 'animate-spin' : ''}`} />
                          {reordering === o.id ? 'Adding…' : 'Reorder'}
                        </Button>
                        {['confirmed', 'assigned', 'picked_up', 'out_for_delivery'].includes(o.status) && o.order_number && (
                          <Link
                            to={`/track/${o.order_number}`}
                            className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 text-sm font-medium inline-flex items-center transition"
                            data-testid={`track-order-btn-${o.id}`}
                          >
                            <Navigation className="w-4 h-4 mr-1" /> Track order
                          </Link>
                        )}
                        <Button
                          onClick={() => download(o)}
                          className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white"
                          data-testid={`download-invoice-btn-${o.id}`}
                        >
                          <Download className="w-4 h-4 mr-1" /> Download invoice (PDF)
                        </Button>
                        <div className="text-xs text-neutral-500">
                          {o.customer_address && <>Ship to: {o.customer_address}</>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
};

export default MyOrders;
